import { html, TemplateResult } from 'lit';
import { OutlineCard } from '../../base/outline-card/outline-card';
import { customElement, property, query } from 'lit/decorators.js';
import componentStyles from './idb-image-card.css.lit';
import { classMap } from 'lit/directives/class-map.js';
import { emit, waitForEvent } from '../../../internal/event';
import { watch } from '../../../internal/watch';
import { SlotController } from '../../controllers/slot-controller';
import { MobileController } from '../../controllers/mobile-controller';
import '../idb-heading/idb-heading';
import { LinkTargetType } from 'src/components/base/outline-link/config';
import { getUrlTarget } from '../../../utilities/url_utils';

/**The IDB image card component.
 * @element idb-image-card
 * @property url — The property to set the href/url for the card
 * @attr imageTemplate — sets up the image for a background
 * @slot heading slot - the title portion of the header
 * @slot image slot - for the background image
 * @todo see if there's a way to get the description to show/hide when resizing not just refreshing
 */

export type Numbers =
  | 'number-1'
  | 'number-2'
  | 'number-3'
  | 'number-4'
  | 'number-5';

export type BgColor = 'red' | 'dark-blue' | 'green' | 'purple' | 'light-blue';

@customElement('idb-image-card')
export class ImageCard extends OutlineCard {
  static styles = [componentStyles];
  slots = new SlotController(
    this, // This, the host element.
    true // true // To shift or not to shift LightDom nodes to ShadowDOM.
  );
  private mobileController = new MobileController(this, 'lg');

  @query('.description') panel: HTMLElement;

  /** Indicates whether or not the dropdown is open. You can use this in lieu of the show/hide methods. */
  @property({ type: Boolean, reflect: true }) open = false;

  /** Disables the dropdown so the panel will not open. */
  @property({ type: Boolean }) disabled = false;

  /**
   * Enable this option to prevent the panel from being clipped when the component is placed inside a container with
   * `overflow: auto|scroll`.
   */
  @property({ type: Boolean }) hoist = false;

  @property({ type: Boolean, attribute: 'block-pillars' })
  block_pillars = false;

  @property({ type: Boolean, attribute: 'image-background' })
  imageBackground = false;

  // To keep the numbers, add this to the component
  @property({ type: Boolean, attribute: 'show-number' })
  showNumber = false;

   // To keep background and description visibility, add this to the component
   @property({ type: Boolean, attribute: 'fixed-hover-description' })
   fixHover = false;

  // Choose the bg color for the hover state
  @property({ type: String, attribute: 'hover-color' })
  hoverColor: BgColor = 'light-blue';

  /**
   * The url for the card to link too.
   */
  @property({ type: String, reflect: true, attribute: 'url' })
  url: string;

  /**
   * The button target for the link;
   */
  @property({ type: String, reflect: true, attribute: 'link-target' })
  linkTarget: LinkTargetType;

  /**
   * The predefined number to use in the card
   */
  @property({ type: String, reflect: true, attribute: 'number' })
  number: Numbers;

  // Hide the Text until Hovered
  firstUpdated() {
    if (this.panel) {
      if(this.fixHover){
        this.panel.hidden = this.open;
      }
      else{
        this.panel.hidden = !this.open;
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.hide();
  }

  /** Shows the dropdown panel. */
  async show() {
    if(this.fixHover || this.mobileController.isMobile){
      this.open = true;
    }else{
      this.open = false;
      this.panel.hidden = this.open;  
    }

    return waitForEvent(this, 'after-show');
  }

  // /** Hides the dropdown panel */
  async hide() {
    
    if(this.fixHover || this.mobileController.isMobile) {
      this.panel.hidden = false;
    }else{
      this.panel.hidden = true;
    }
    // this.open = false;
    // this.mobileController.isMobile = true;
    return waitForEvent(this, 'after-hide');
  }

  @watch('open', { waitUntilFirstUpdate: true })
  async handleOpenChange() {
    if (this.open) {
      console.log("este es el open ", this.open);
      // Show
      emit(this, 'show');
      emit(this, 'after-show');
    } else {
      // Hide
      emit(this, 'hide');
      emit(this, 'after-hide');
    }
  }

  handleTriggerKeyUp(event: KeyboardEvent) {
    // Adding keyboard control for access to url
    if (event.key == 'Enter') {
      if (this.url) {
        if (this.linkTarget == '_blank') {
          window.open(this.url, '_blank');
        } else {
          window.location.href = this.url;
        }
      }
    } else {
      // Wait for the next element to be selected.
      setTimeout(() => {
        if (document.activeElement !== this) {
          this.hide();
        }
      }, 0);
    }
  }

  handleClick() {
    // Go to URL on click
    if (this.url) {
      if (this.linkTarget == '_blank') {
        window.open(this.url, '_blank');
      } else {
        window.location.href = this.url;
      }
    }
  }

  numberImage(): TemplateResult {
    switch (this.number) {
      case 'number-1':
        return html`
            <div class="number number-1">
              ${
                this.showNumber
                  ? html`
                <svg width="77" height="178" aria-labelledby="number-1" role="img" viewBox="0 0 77 178"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                    <path opacity=".4"
                          d="M76.453 178H47.996V63.687c0-13.643.323-24.46.969-32.453-1.857 1.938-4.158 4.077-6.903 6.418-2.664 2.341-11.705 9.769-27.124 22.282L.648 41.89 52.718.96h23.735V178Z"
                          fill="#fff"/>
                </svg>
                `
                  : ''
              }
            </div>`;
      case 'number-2':
        return html`
                <div class="number number-2">
                  ${
                    this.showNumber
                      ? html`
                    <svg width="121" height="180" aria-labelledby="number-2" role="img" viewBox="0 0 121 180"
                    fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path opacity=".4"
                              d="M120.902 180H.898v-21.555l45.653-45.894c13.482-13.805 22.402-23.573 26.761-29.305 4.44-5.812 7.67-11.262 9.688-16.348 2.018-5.085 3.027-10.535 3.027-16.347 0-7.992-2.421-14.29-7.265-18.89-4.763-4.602-11.383-6.903-19.86-6.903-6.78 0-13.36 1.251-19.738 3.754-6.297 2.502-13.603 7.023-21.918 13.562L1.867 23.304c9.85-8.314 19.416-14.208 28.7-17.679C39.85 2.154 49.74.418 60.233.418c16.47 0 29.668 4.319 39.598 12.957 9.93 8.557 14.895 20.102 14.895 34.633 0 7.992-1.454 15.58-4.36 22.765-2.825 7.185-7.225 14.612-13.199 22.282-5.893 7.588-15.742 17.881-29.547 30.879l-30.758 29.789v1.211h84.039V180Z"
                              fill="#fff"/>
                    </svg>
                    `
                      : ''
                  }
                </div>
                `;
      case 'number-3':
        return html`
                <div class="number number-3">
                ${
                  this.showNumber
                    ? html`
                    <svg width="120" height="183" aria-labelledby="number-3" role="img" viewBox="0 0 120 183"
                    fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path opacity=".4"
                              d="M114.242 43.648c0 11.222-3.269 20.586-9.808 28.094-6.54 7.427-15.743 12.432-27.61 15.016v.969c14.209 1.776 24.865 6.216 31.969 13.32 7.104 7.023 10.656 16.388 10.656 28.094 0 17.033-6.014 30.192-18.043 39.476-12.028 9.203-29.143 13.805-51.344 13.805-19.617 0-36.166-3.189-49.648-9.567v-25.308c7.508 3.713 15.46 6.579 23.855 8.598 8.396 2.018 16.47 3.027 24.22 3.027 13.723 0 23.976-2.543 30.757-7.629 6.781-5.086 10.172-12.957 10.172-23.613 0-9.446-3.754-16.388-11.262-20.828-7.508-4.441-19.294-6.661-35.36-6.661H27.419V77.312H43.04c28.255 0 42.383-9.768 42.383-29.304 0-7.589-2.462-13.442-7.387-17.559-4.924-4.117-12.19-6.176-21.797-6.176-6.7 0-13.159.97-19.375 2.907-6.216 1.857-13.562 5.53-22.039 11.02L.898 18.34C17.125 6.392 35.975.418 57.45.418c17.841 0 31.767 3.835 41.778 11.504 10.01 7.67 15.015 18.245 15.015 31.726Z"
                              fill="#fff"/>
                    </svg>
                    `
                    : ''
                }
                </div>
                    `;
      case 'number-4':
        return html`
                <div class="number number-4">
                ${
                  this.showNumber
                    ? html`
                    <svg width="134" height="178" aria-labelledby="number-4" role="img" viewBox="0 0 134 178"
                    fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path opacity=".4"
                              d="M133.199 139.371h-23.855V178h-27.73v-38.629H.722v-21.918L81.613.477h27.731v115.281h23.855v23.613Zm-51.586-23.613V71.316c0-15.823.404-28.78 1.211-38.87h-.968c-2.26 5.327-5.813 11.786-10.657 19.374l-43.957 63.938h54.371Z"
                              fill="#fff"/>
                    </svg>
                    `
                    : ''
                }
                </div>`;
      case 'number-5':
        return html`
              <div class="number number-5">
              ${
                this.showNumber
                  ? html`
                  <svg width="115" height="181" aria-labelledby="number-5" role="img"
                  viewBox="0 0 115 181" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path opacity=".4"
                            d="M56.96 67.441c17.842 0 31.97 4.723 42.384 14.168 10.414 9.446 15.621 22.322 15.621 38.629 0 18.891-5.934 33.664-17.8 44.321-11.787 10.575-28.58 15.863-50.376 15.863-19.779 0-35.319-3.189-46.621-9.567v-25.793c6.539 3.714 14.047 6.58 22.523 8.598 8.477 2.018 16.348 3.028 23.614 3.028 12.836 0 22.604-2.866 29.304-8.598 6.7-5.732 10.051-14.128 10.051-25.188 0-21.151-13.482-31.726-40.445-31.726-3.794 0-8.477.403-14.047 1.21-5.57.727-10.454 1.575-14.652 2.544L3.8 87.422 10.582.96h92.031v25.31H35.648l-3.996 43.836c2.826-.485 6.257-1.05 10.293-1.696 4.117-.646 9.123-.969 15.016-.969Z"
                            fill="#fff"/>
                  </svg>
                  `
                  : ''
              }
              </div>`;
    }
  }

  /**
   * Method to handle the number image add ons
   *
   * @returns TemplateResult
   */

  render(): TemplateResult {
    this.linkTarget = getUrlTarget(this.url, this.linkTarget);
    const cardClasses = {
      'idb-image-card': true,
      'nolink': !this.url,
      // 'mobile': this.mobileController.isMobile,
      [`hover-color--${this.hoverColor}`]: this.hoverColor,
    };

    const wrapperClasses = {
      // 'not-wrapper-mobile': !this.mobileController.isMobile,
      'wrapper-mobile': this.mobileController.isMobile,
      'fixed-hover-description': this.fixHover,
    };
    const dropdown = {
      'description--open': this.open || this.fixHover, 
      'mobile': this.mobileController.isMobile,
    };

    return html`
        <section class="${classMap(cardClasses)} ">
            <div class="link ${this.block_pillars ? 'block-pillar' : ''}"
              tabindex="0"
              aria-label="Click/Enter to learn more"
              @mouseenter="${this.show}"
              @mouseleave="${this.hide}"
              @keydown="${this.handleTriggerKeyUp}"
              @focus="${this.show}"
              @click="${this.handleClick}"
            >
                ${
                  this.imageBackground
                    ? html`
                        <div class="gray-overlay"></div>
                        <slot name="image"></slot>`
                    : html`
                        <div class="gradient-background"/>`
                }
              <div class="description__trigger ${classMap(dropdown)}">
                <div tabindex="0" class="text-wrapper ${classMap(
                  wrapperClasses
                )}" @focus="${this.show}">
                  <div id="${this.number}" class="heading">
                      <idb-heading level="h3" level-size="3xl" level-style="bold">
                        <slot name="heading"></slot>
                      </idb-heading>
                  </div>
                  <div class="description ${classMap(dropdown)}" aria-hidden=${
      this.open
    } aria-labelledby="dropdown">
                    <slot name="description"></slot>
                  </div>
                    ${this.numberImage()}
                </div>
              </div>
            </div>
        </section>
        `;
  }
}
